import { initializeApp } from "firebase/app"
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
  addDoc,
  updateDoc,
  serverTimestamp,
  increment,
  initializeFirestore,
  orderBy,
  onSnapshot,
  writeBatch,
} from "firebase/firestore"
import { firebaseConfig, chatCollectionModel} from "./firebase-config"
import { post } from "../helpers/config";
import { IChatDetail } from "../types/user";
interface NotificationData {
  token: string;
  userID?: string;
  deviceType: string;
  title: string;
  body: string;
  data: { [key: string]: string };
}

// Initialize Firebase
const app = initializeApp(firebaseConfig) 

export const db = initializeFirestore(app, {
  experimentalForceLongPolling: true,
})

export class FirestoreDatabaseService {
  // Get or create a chat ID between two users
  async getOrCreateChatId(adminId: string, otherParticipantId: string, isAdminMessaged: boolean): Promise<string> {
    try {
      console.log("chatCollectionModel.chats:", chatCollectionModel.adminChats);

      // Check if a chat already exists
      const chatsRef = collection(db, chatCollectionModel.adminChats!)
      const q = query(chatsRef, where("participants", "array-contains", adminId))
      const querySnapshot = await getDocs(q)

      for (const doc of querySnapshot.docs) {
        const participants = doc.data().participants as string[]
        if (participants.includes(otherParticipantId)) {
          return doc.id
        }
      }

      // Create a new chat if it doesn't exist
      const chatId = `${adminId}_${otherParticipantId}`
      const chatRef = doc(db, chatCollectionModel.adminChats!, chatId)

      await setDoc(chatRef, {
        participants: [adminId, otherParticipantId],
        lastUpdated: Date.now(),
        lastMessagedBy: isAdminMessaged ? otherParticipantId : adminId,
        unreadCount: {
          [adminId]: 0,
          [otherParticipantId]: 0,
        },
      })

      // Add the chat reference to both users
      const adminChatRef = doc(db, chatCollectionModel.adminUser!, adminId, chatCollectionModel.adminChats!, chatId)
      await setDoc(adminChatRef, { chatId })

      const otherParticipantChatRef = doc(db, chatCollectionModel.adminUser!, otherParticipantId, chatCollectionModel.adminChats!, chatId)
      await setDoc(otherParticipantChatRef, { chatId })

      return chatId
    } catch (error) {
      console.error("Error in getOrCreateChatId:", error)
      throw error
    }
  }

  async getOrCreateChatIdForTrip(adminId: string, agentId: string, travellerId: string): Promise<string>{
    try{
        const chatsRef = collection(db, chatCollectionModel.chats)
        const q = query(chatsRef, where("participants", "array-contains", adminId))
        const querySnapshot = await getDocs(q)

        for(const doc of querySnapshot.docs){
          const participants = doc.data().participants as string[];
          if(participants.includes(agentId || travellerId)){
            return doc.id;
          }
        }
        const chatId = `${travellerId}_${agentId}_${adminId}`
        const chatRef = doc(db, chatCollectionModel.chats, chatId)

         await setDoc(chatRef, {
          participants: [adminId, agentId, travellerId],
          lastUpdated: Date.now(),
          lastMesagedBy: adminId,
          unreadCount: {
            [adminId]: 0,
            [agentId]: 0,
            [travellerId]: 0
          },
        })

        //add the chatref to all users
        const travellerChatRef = doc(db, chatCollectionModel.user!, travellerId, chatCollectionModel.chats!, chatId);
        await setDoc(travellerChatRef, {chatId})

        const agentChatRef = doc(db, chatCollectionModel.user!, agentId, chatCollectionModel.chats!,chatId);
        await setDoc(agentChatRef, {chatId})

        const adminChatRef = doc(db, chatCollectionModel.user!, adminId, chatCollectionModel.chats!, chatId);
        await setDoc(adminChatRef, {chatId})

        return chatId;
    }
    catch(error) {
        console.error("Error in getOrCreateChatId:", error)
        throw error
    }
  }

  // Add user to Firestore
  async addUser(userData: any): Promise<boolean> {
    try {
      const userRef = doc(db, chatCollectionModel.adminUser!, userData.userId)
      await setDoc(userRef, userData)
      return true
    } catch (error) {
      console.error("Error adding user:", error)
      return false
    }
  }

  // Update user in Firestore
  async updateUser(userId: string, userData: any): Promise<boolean> {
    try {
      const userRef = doc(db, chatCollectionModel.adminUser!, userId)
      await updateDoc(userRef, userData)
      return true
    } catch (error) {
      console.error("Error updating user:", error)
      return false
    }
  }

  // Get user from Firestore
  async getUser(userId: string): Promise<any> {
    try {
      const userRef = doc(db, chatCollectionModel.adminUser!, userId)
      const userSnap = await getDoc(userRef)

      if (userSnap.exists()) {
        return userSnap.data()
      } else {
        console.log("No such user!")
        return null
      }
    } catch (error) {
      console.error("Error getting user:", error)
      return null
    }
  }

  // Get chat list for a user
  async getChatList(userId: string): Promise<any[]> {
    try {
      const userChatsRef = collection(db, chatCollectionModel.adminUser!, userId, chatCollectionModel.adminChats!)
      const userChatsSnap = await getDocs(userChatsRef)

      const chatList = []

      for (const doc1 of userChatsSnap.docs) {
        const chatId = doc1.data().chatId
        const chatRef = doc(db, chatCollectionModel.adminChats!, chatId)
        const chatSnap = await getDoc(chatRef)

        if (chatSnap.exists()) {
          const chatData = chatSnap.data()
          chatList.push({
            chatId,
            participants: chatData.participants,
            unreadCount: chatData.unreadCount[userId],
            lastUpdated: chatData.lastUpdated,
          })
        }
      }

      return chatList
    } catch (error) {
      console.error("Error getting chat list:", error)
      return []
    }
  }

  // Send a message
  async sendMessage(chatId: string, senderId: string, receiverId: string, message: string,  imageURL?: string, senderName?: string): Promise<boolean> {
    try {
      // Add message to Firestore
      const messagesRef = collection(db, chatCollectionModel.adminChats!, chatId, "messages")
     const messageData: any = {
        text: message,
        senderId,
        timestamp: Date.now(),
        sortingTimeStamp: serverTimestamp(),
        read: false,
      }
     if (imageURL) {
        messageData.imageURL = imageURL
      }
      await addDoc(messagesRef, messageData);
       
      // Update chat metadata
      const chatRef = doc(db, chatCollectionModel.adminChats!, chatId)
      await updateDoc(chatRef, {
        lastUpdated: Date.now(),
        lastMessagedBy: senderId,
        [`unreadCount.${receiverId}`]: increment(1),
      })
    
      try {
        const receiverDoc = await getDoc(doc(db, chatCollectionModel.adminUser!, receiverId))
        if (receiverDoc.exists()) {
          const receiverData = receiverDoc.data()
          const receiverToken = receiverData.fcmToken
          const deviceType = receiverData.deviceType

          if (receiverToken) {
          const data: Record<string, string> = {
              name: senderName || "Support",
              chatId: chatId,
              adminId: senderId,
              otherId: receiverId,
              currentUserId: senderId,
            }

        const notificationData: NotificationData = {
            token: receiverToken,
              deviceType: deviceType, 
              title: `Incoming message from ${senderName || "Support"}`,
              body: message || "",
              data,
            };

          await this.sendNotification(notificationData);
           }
        }
      } catch (notificationError) {
        console.error("Error sending notification:", notificationError)
      }
      return true
      
    } catch (error) {
      console.error("Error sending message:", error)
      return false
    }
  }

  async sendChatMessage(params: {
  chatId: string;
  senderId: string;
  senderName: string;
  text: string;
  participants: string[];
}): Promise<void> {
  try {
    const { chatId, senderId, senderName, text, participants } = params;
    // Add message to messages subcollection
    const messagesRef = collection(
      db, 
      chatCollectionModel.chats, 
      chatId, 
      'messages'
    );
    
    await addDoc(messagesRef, {
      senderId,    
      senderName,  
      text,
      timestamp: Date.now(),
      sortingTimeStamp: serverTimestamp(),
      read: false,
      type: 'text'
    });

    // Update chat metadata
    const chatRef = doc(db, chatCollectionModel.chats, chatId);
    const unreadUpdate: any = {};
    
    // Increment unread count for all participants except sender
    participants.forEach(participantId => {
      if (participantId !== senderId) {
        unreadUpdate[`unreadCount.${participantId}`] = increment(1);
      }
    });

    await updateDoc(chatRef, {
      lastUpdated: Date.now(),
      lastMessagedBy: senderId,
      ...unreadUpdate
    });

      for (const participantId of participants) {
      if (participantId === senderId) continue;
        console.log(participantId);
      try {
        const receiverSnap = await getDoc(
          doc(db, chatCollectionModel.user!, participantId)
        );

        if (!receiverSnap.exists()) continue;

        const receiverData = receiverSnap.data();
        const receiverToken = receiverData.fcmToken;
        const deviceType = receiverData.deviceType;

        if (!receiverToken) continue;

        const notificationData: NotificationData = {
          token: receiverToken,
          userID: participantId,
          deviceType,
          title: `Incoming message from ${senderName || "Support"}`,
          body: text,
          data: {
            chatId,
            senderId,
            receiverId: participantId,
          },
        };

        await this.sendNotification(notificationData);
      } catch (notificationError) {
        console.error(
          `Notification failed for user ${participantId}`,
          notificationError
        );
      }
    }
  } catch (error) {
    console.error("Error sending message:", error);
    throw error;
  }
}
  

  private async sendNotification(data: NotificationData): Promise<void> {
    try {
      await post("notification/send-notification", data);
    } catch (error) {
      console.error("Failed to send Firebase notification:", error);
    }
  }

}

export default new FirestoreDatabaseService()

export const getLiveMessages = (
  chatId: string,
  currentUserId: string,
  onChange: (messages: IChatDetail[]) => void
) => {

  console.log(chatCollectionModel.chats);
  const q = query(
    collection(db, chatCollectionModel.chats, chatId, "messages"),
    orderBy("sortingTimeStamp", "asc")
  );

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const msgs: IChatDetail[] = snapshot.docs.map(doc => ({
      id: doc.id,
      ...(doc.data() as IChatDetail)
    }));

    onChange(msgs); 
  });

  return unsubscribe; 
};

/**
 * Listen to unread count for a single chat (real-time)
 */
export const listenToUnreadCount = (
  chatId: string,
  userId: string,
  callback: (count: number) => void
) => {
  const messagesRef = collection(db, chatCollectionModel.chats, chatId, 'messages');
  
  // Query only for unread messages (no != operator needed)
  const unreadQuery = query(
    messagesRef,
    where('read', '==', false)
  );

  const unsubscribe = onSnapshot(
    unreadQuery,
    (snapshot) => {
      // Client-side filtering: exclude messages sent by current user
      const unreadCount = snapshot.docs.filter(doc => {
        const data = doc.data();
        return data.senderId !== userId;
      }).length;
      
      callback(unreadCount);
    },
    (error) => {
      console.error('Error listening to unread count:', error);
      callback(0);
    }
  );

  return unsubscribe;
};

/**
 * Listen to unread counts for multiple chats
 */
export const listenToMultipleUnreadCounts = (
  chatIds: string[],
  userId: string,
  callback: (chatId: string, count: number) => void
) => {
  const unsubscribers: { [chatId: string]: () => void } = {};

  chatIds.forEach((chatId) => {
    unsubscribers[chatId] = listenToUnreadCount(chatId, userId, (count) => {
      callback(chatId, count);
    });
  });

  // Return a function to unsubscribe all listeners
  return () => {
    Object.values(unsubscribers).forEach((unsubscribe) => unsubscribe());
  };
};

/**
 * Mark all unread messages as read in a chat
 */
export const markMessagesAsRead = async (
  chatId: string,
  userId: string
): Promise<void> => {
  try {
    const messagesRef = collection(db, chatCollectionModel.chats, chatId, 'messages');
    
    // Get all unread messages 
    const unreadQuery = query(
      messagesRef,
      where('read', '==', false)
    );

    const snapshot = await getDocs(unreadQuery);

    if (snapshot.empty) {
      return; 
    }

    const batch = writeBatch(db);
    let updateCount = 0;

    // Client-side filtering: only mark messages NOT sent by current user
    snapshot.docs.forEach((document) => {
      const data = document.data();
      if (data.senderId !== userId) {
        batch.update(document.ref, { 
          read: true
        });
        updateCount++;
      }
    });

    if (updateCount > 0) {
      await batch.commit();
      console.log(`Marked ${updateCount} messages as read in chat ${chatId}`);
    }
  } catch (error) {
    console.error('Error marking messages as read:', error);
    throw error;
  }
};

/**
 * Get unread count for a chat (one-time fetch)
 */
export const getUnreadCount = async (
  chatId: string,
  userId: string
): Promise<number> => {
  try {
    const messagesRef = collection(db, chatCollectionModel.chats, chatId, 'messages');
    
    const unreadQuery = query(
      messagesRef,
      where('read', '==', false)
    );

    const snapshot = await getDocs(unreadQuery);
    
    const unreadCount = snapshot.docs.filter(doc => {
      const data = doc.data();
      return data.senderId !== userId;
    }).length;
    
    return unreadCount;
  } catch (error) {
    console.error('Error getting unread count:', error);
    return 0;
  }
};

